import React from 'react';
import { TableInstance, ColumnInstance } from 'react-table';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';

type VisibilityColumn = ColumnInstance<TableRow> & { canHide?: boolean };

export interface ColumnVisibilityControlProps {
  tableInstance: TableInstance<TableRow>;
  /** When true, toggles persist via onHiddenChange and the Reset button shows */
  persistColumnPrefs?: boolean;
  /** Called with the updated hidden-column ids after each toggle */
  onHiddenChange?: (hiddenColumnIds: string[]) => void;
  /** Called when the user clicks Reset to defaults */
  onReset?: () => Promise<void> | void;
}

const getColumnLabel = (column: ColumnInstance<TableRow>): string => {
  if (typeof column.Header === 'string') return column.Header;
  if (typeof column.id === 'string') {
    return column.id.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return 'Column';
};

const ColumnVisibilityControl: React.FC<ColumnVisibilityControlProps> = ({
  tableInstance,
  persistColumnPrefs = false,
  onHiddenChange,
  onReset
}) => {
  const { allColumns } = tableInstance;
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [search, setSearch] = React.useState('');
  const [resetting, setResetting] = React.useState(false);

  const toggleableColumns = React.useMemo(
    () =>
      (allColumns as VisibilityColumn[]).filter(
        (column) => column.id !== '_selector' && column.canHide !== false
      ),
    [allColumns]
  );

  const filteredColumns = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return toggleableColumns;
    return toggleableColumns.filter((column) => getColumnLabel(column).toLowerCase().includes(query));
  }, [toggleableColumns, search]);

  const visibleCount = React.useMemo(
    () => toggleableColumns.filter((column) => column.isVisible).length,
    [toggleableColumns]
  );

  if (!toggleableColumns.length) {
    return null;
  }

  const reportHidden = () => {
    if (!persistColumnPrefs || !onHiddenChange) return;
    // Read directly off allColumns (post-toggle) so we capture the latest state.
    const hidden = (allColumns as VisibilityColumn[])
      .filter((c) => c.id !== '_selector' && c.isVisible === false)
      .map((c) => String(c.id));
    onHiddenChange(hidden);
  };

  const handleToggle = (columnId: string) => {
    const column = toggleableColumns.find((col) => col.id === columnId);
    if (!column) return;
    if (column.isVisible && visibleCount <= 1) return;
    column.toggleHidden(column.isVisible);
    // toggleHidden updates synchronously; report next tick to read updated isVisible.
    setTimeout(reportHidden, 0);
  };

  const handleShowAll = () => {
    toggleableColumns.forEach((column) => {
      if (!column.isVisible) {
        column.toggleHidden(false);
      }
    });
    setTimeout(reportHidden, 0);
  };

  const handleReset = async () => {
    if (!onReset) return;
    setResetting(true);
    try {
      await onReset();
      // Re-apply defaults from the column metadata (canHide / defaultHidden).
      (allColumns as (VisibilityColumn & { defaultHidden?: boolean })[]).forEach((column) => {
        if (column.id === '_selector' || column.canHide === false) return;
        const shouldHide = Boolean(column.defaultHidden);
        if (column.isVisible !== !shouldHide) column.toggleHidden(shouldHide);
      });
      setAnchorEl(null);
    } finally {
      setResetting(false);
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title="Choose visible columns" arrow>
        <IconButton
          size="small"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          data-testid="column-visibility-trigger">
          <ViewColumnIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 280, p: 2 } }}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
            Columns
          </Typography>
          <TextField
            size="small"
            placeholder="Search columns"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />
          <Box sx={{ maxHeight: 260, overflowY: 'auto', pr: 1 }}>
            <FormGroup>
              {filteredColumns.map((column) => {
                const label = getColumnLabel(column);
                const disableUncheck = column.isVisible && visibleCount <= 1;
                return (
                  <FormControlLabel
                    key={column.id}
                    control={
                      <Checkbox
                        size="small"
                        checked={column.isVisible}
                        disabled={disableUncheck}
                        onChange={() => handleToggle(column.id as string)}
                      />
                    }
                    label={<Typography variant="body2">{label}</Typography>}
                  />
                );
              })}
              {!filteredColumns.length && (
                <Typography variant="body2" color="text.secondary">
                  No columns match &ldquo;{search}&rdquo;.
                </Typography>
              )}
            </FormGroup>
          </Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="text.secondary">
              {visibleCount} of {toggleableColumns.length} visible
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={handleShowAll}>
                Show all
              </Button>
              {persistColumnPrefs && onReset && (
                <Button
                  size="small"
                  color="warning"
                  disabled={resetting}
                  onClick={handleReset}>
                  Reset
                </Button>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};

export default ColumnVisibilityControl;
